import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/cn';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[0.1875rem] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary:
          ' bg-primary text-primary-foreground hover:shadow-[1px_5px_10px_-1px_var(--primary)]',
        outlinePrimary:
          'border-solid border-primary text-primary border-2 bg-transparent transition-all  hover:shadow-[1px_9px_18px_-1px_var(--primary)]',
        outlineAccent:
          'border-solid border-accent text-accent border-2 bg-transparent transition-all hover:shadow-[1px_9px_18px_-1px_var(--accent)]',
        secondary:
          'bg-secondary text-secondary-foreground hover:shadow-[1px_9px_18px_-1px_var(--secondary)]',
        accent:
          'bg-accent text-accent-foreground hover:shadow-[1px_9px_18px_-1px_var(--accent)]',
        destructive:
          'bg-destructive/15 text-destructive-foreground hover:shadow-[1px_9px_18px_-1px_var(--destructive)]',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary hover:brightness-60',
      },
      size: {
        sm: 'h-10 px-4 py-2.5 text-sm font-medium',
        md: 'h-13.5 lg:py-2.25 lg:h-15 px-7.5 py-1.5 leading-10.5 text-base font-medium',
        'icon-sm': 'size-6',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export function Button({
  className,
  variant,
  size,
  asChild = false,
  animate = false,
  disableHover = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    animate?: boolean;
    disableHover?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({ variant, size }),
        animate &&
          'overflow-hidden transition-all duration-400 ease-in-out hover:shadow-none active:scale-95 [&>*:not(svg)]:relative [&>*:not(svg)]:transition-all [&>*:not(svg)]:duration-400 hover:[&>*:not(svg)]:translate-x-[150%] [&>svg]:relative [&>svg]:left-0 [&>svg]:grid [&>svg]:place-items-center [&>svg]:transition-all [&>svg]:duration-400 hover:[&>svg]:left-1/2 hover:[&>svg]:-translate-x-[50%] hover:[&>svg]:scale-110 hover:[&>svg]:rotate-45',
        className,
      )}
      {...props}
    />
  );
}

export { buttonVariants };
